import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    Button,
    Stepper,
    Step,
    StepLabel,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Card,
    CardContent,
    Chip,
    Alert,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    Switch,
    Autocomplete
} from '@mui/material';
import {
    Close as CloseIcon,
    Casino as CasinoIcon,
    ArrowForward as ArrowForwardIcon,
    ArrowBack as ArrowBackIcon,
    Check as CheckIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getActiveSymbols } from '../../api/symbolApi';
import { getAllUsers } from '../../api/admin_api';
import { getAccountsByUserId } from '../../api/account_api';
import { addStrategy } from '../../api/strategyApi';
import { getAllTemplates, getTemplateById } from '../../templates/strategies';

const CreateStrategyWizard = ({ open, onClose, onSave, template, preSelectedUser, preSelectedAccount }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [loadingSymbols, setLoadingSymbols] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [symbols, setSymbols] = useState({ futures: [], spot: [] });
    const [users, setUsers] = useState([]);
    const [accounts, setAccounts] = useState([]);

    // D1/D2: Validation errors state
    const [validationErrors, setValidationErrors] = useState({});
    const [formData, setFormData] = useState({
        // Step 1: Basic Info
        userId: '',
        userName: '',
        version: 'V2',
        selectedTemplateId: '', // Selected template ID from dropdown
        strategyType: '', // Strategy type: grid, basic, etc.
        strategyName: '',
        description: '',
        accountNumber: '',
        magicId: '',
        dashboardType: 'Algo.01', // Default to Legacy dashboard
        isMultiSymbol: true, // Default to multi-symbol

        // Step 2: Symbols
        futuresSymbol: '',
        spotSymbol: '',
        selectedSymbols: [], // Array to store multiple selected symbols

        // Step 3: V2 Parameters
        bidirectionalMode: false,
        maxTotalLotBothDirections: '0.30',
        useBenchmarkMode: false,
        benchmarkThresholdBuy: '4.0',
        benchmarkThresholdSell: '4.0',
        gridThreshold: '2',
        maxGridLevels: '5',
        buyBaseLotSize: '0.01',
        sellBaseLotSize: '0.01',
        buyLotSizeMultiplier: '1.2',
        sellLotSizeMultiplier: '1.2',
        v2MaxLotOpenedAllowed: '0.15',
        v2TpGridEnabled: true,
        tpGridPips: '10',
        tpMode: 'AverageSpread',
        v2StopLossEnabled: true,
        stopLossPips: '20',
        buyGrid: '1.500',
        sellGrid: '1.500',
        buyLegTP: '0.300',
        sellLegTP: '0.300',
        buyLegSL: '0.500',
        sellLegSL: '0.500',
        accumulativeTP: '2.5',
        accumulativeSL: '-10.0',
        riskManagementEnabled: false,
        spreadValidationEnabled: true,
        minAcceptableSpread: '-1000',
        maxAcceptableSpread: '1000',

        // Step 3: V1 Parameters
        spreadDirection: 'Buy Spread',
        baseLotSize: '0.01',
        minLotSize: '0.01',
        maxLotOpenedAllowed: '0.05',
        gridThreshold: '2',
        maxGridLevels: '3',
        tpGridPips: '10',
        stopLossPips: '20',
        tpGridEnabled: false,
        stopLossEnabled: false,
        allowGridReopening: false,
        resetGridOnFullClose: false,
        orderTimeInForce: 'FOK',
        openNow: 'none',

        // Step 4: Review
        reviewed: false
    });

    const steps = ['Basic Information', 'Select Symbols', 'Trading Parameters', 'Review & Create'];

    // Prevent mouse wheel from changing number input values
    const handleNumberInputWheel = (e) => {
        e.target.blur();
    };

    // Fetch all data when dialog opens
    useEffect(() => {
        if (open) {
            fetchUsers();
            fetchSymbols();
        }
    }, [open]);

    // Fetch accounts when user is selected
    useEffect(() => {
        if (formData.userId) {
            fetchAccounts(formData.userId);
        } else {
            setAccounts([]);
        }
    }, [formData.userId]);

    // Initialize form data with template defaults when template is provided
    useEffect(() => {
        if (template && open) {
            const templateDefaults = {
                // Pre-fill Step 1 fields from template
                selectedTemplateId: template.templateId || template.strategyType || '', // ✅ Set template ID for validation
                strategyName: template.name || '',
                description: template.description || '',
                strategyType: template.strategyType || 'grid',
                version: template.version === 'v1' ? 'V1' : 'V2',
                isMultiSymbol: template.isMultiSymbol !== undefined ? template.isMultiSymbol : true
            };

            // Extract symbols from template parameters for Step 2
            let futuresSymbol = '';
            let spotSymbol = '';

            if (template.parameters) {
                template.parameters.forEach(param => {
                    if (param.defaultValue !== undefined) {
                        templateDefaults[param.name] = param.defaultValue;
                    }

                    // Extract symbol values
                    if (param.name === 'FuturesSymbol' && param.defaultValue) {
                        futuresSymbol = param.defaultValue;
                    } else if (param.name === 'SpotSymbol' && param.defaultValue) {
                        spotSymbol = param.defaultValue;
                    }
                });
            }

            // Pre-fill selectedSymbols array for Step 2
            const selectedSymbols = [];
            if (futuresSymbol) selectedSymbols.push(futuresSymbol);
            if (spotSymbol) selectedSymbols.push(spotSymbol);

            if (selectedSymbols.length > 0) {
                templateDefaults.selectedSymbols = selectedSymbols;
                templateDefaults.futuresSymbol = futuresSymbol;
                templateDefaults.spotSymbol = spotSymbol;
            }

            setFormData(prev => ({ ...prev, ...templateDefaults }));
        }
    }, [template, open]);

    // Pre-fill user and account when provided from AssignStrategyContent
    useEffect(() => {
        if (open && preSelectedUser && users.length > 0) {
            // Find user by email
            const user = users.find(u => u.email === preSelectedUser);
            if (user && user.id) {
                setFormData(prev => ({
                    ...prev,
                    userId: user.id,
                    userName: `${user.firstName || user.name || ''} ${user.lastName || ''}`.trim()
                }));
            }
        }
    }, [open, preSelectedUser, users]);

    // Pre-fill account when provided from AssignStrategyContent
    useEffect(() => {
        if (open && preSelectedAccount) {
            setFormData(prev => ({
                ...prev,
                accountNumber: preSelectedAccount
            }));
        }
    }, [open, preSelectedAccount]);

    const fetchUsers = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error('Authentication token not found');
            return;
        }

        try {
            setLoadingUsers(true);
            const response = await getAllUsers();

            // Handle different response formats
            const usersData = Array.isArray(response) ? response : (response.data || []);

            setUsers(usersData);
            console.log('Loaded users from API:', usersData);
        } catch (error) {
            console.error('Failed to load users:', error);
            toast.error('Failed to load users');
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchAccounts = async (userId) => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error('Authentication token not found');
            return;
        }

        try {
            setLoadingAccounts(true);
            const response = await getAccountsByUserId(token, userId);

            // Handle different response formats
            const accountsData = response.success ? response.data : (Array.isArray(response) ? response : []);

            setAccounts(accountsData);
            console.log('Loaded accounts from API:', accountsData);
        } catch (error) {
            console.error('Failed to load accounts:', error);
            toast.error('Failed to load accounts for selected user');
            setAccounts([]);
        } finally {
            setLoadingAccounts(false);
        }
    };

    const fetchSymbols = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error('Authentication token not found');
            return;
        }

        try {
            setLoadingSymbols(true);
            const symbolsData = await getActiveSymbols(token);

            // Transform API response to match component format
            // Assuming API returns array of objects with 'name' property
            const symbolNames = Array.isArray(symbolsData)
                ? symbolsData.map(s => s.name || s.lpSymbolName || s.fixSymbol)
                : [];

            setSymbols({
                futures: symbolNames,
                spot: symbolNames
            });

            console.log('Loaded symbols from API:', symbolNames);
        } catch (error) {
            console.error('Failed to load symbols:', error);
            toast.error('Failed to load symbols. Using default list.');

            // Fallback to default symbols if API fails
            setSymbols({
                futures: ['XAUUSD', 'XAGUSD', 'BTCUSD', 'ETHUSD', 'EURUSD', 'GBPUSD'],
                spot: ['XAUUSD', 'XAGUSD', 'BTCUSD', 'ETHUSD', 'EURUSD', 'GBPUSD']
            });
        } finally {
            setLoadingSymbols(false);
        }
    };

    // D1: Validate Step 1 required fields
    const validateStep1 = () => {
        const errors = {};

        if (!formData.userId) {
            errors.userId = 'User selection is required';
        }
        if (!formData.selectedTemplateId) {
            errors.selectedTemplateId = 'Strategy template is required';
        }
        if (!formData.strategyName || formData.strategyName.trim().length < 3) {
            errors.strategyName = 'Strategy name is required (min 3 characters)';
        }
        if (!formData.accountNumber) {
            errors.accountNumber = 'Account selection is required';
        }
        if (!formData.dashboardType) {
            errors.dashboardType = 'Dashboard type is required';
        }

        return errors;
    };

    // D2: Validate Step 2 required fields (Symbols)
    const validateStep2 = () => {
        const errors = {};

        if (!formData.selectedSymbols || formData.selectedSymbols.length < 2) {
            errors.selectedSymbols = 'At least two symbols are required (Futures and Spot)';
        }

        return errors;
    };

    // Validate current step
    const validateCurrentStep = () => {
        let errors = {};

        if (activeStep === 0) {
            errors = validateStep1();
        } else if (activeStep === 1) {
            errors = validateStep2();
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleNext = () => {
        // Validate current step before proceeding
        if (!validateCurrentStep()) {
            // Show toast notification for validation errors
            toast.error('Please fill in all required fields');
            return;
        }

        // Clear validation errors for next step
        setValidationErrors({});

        // When moving from Step 2 to Step 3, map selectedSymbols to futuresSymbol and spotSymbol
        if (activeStep === 1 && formData.selectedSymbols.length > 0) {
            setFormData(prev => ({
                ...prev,
                futuresSymbol: prev.selectedSymbols[0] || '',
                spotSymbol: prev.selectedSymbols[1] || prev.selectedSymbols[0] || ''
            }));
        }
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Helper function to render dynamic template fields
    const renderDynamicField = (param) => {
        // Get value from formData, fallback to default value from template
        const value = formData[param.name] !== undefined && formData[param.name] !== null && formData[param.name] !== ''
            ? formData[param.name]
            : (param.defaultValue !== undefined ? param.defaultValue : '');

        if (param.type === 'select') {
            return (
                <Grid item xs={12} sm={6} key={param.name}>
                    <FormControl fullWidth>
                        <InputLabel>{param.label}</InputLabel>
                        <Select
                            value={value}
                            label={param.label}
                            onChange={(e) => handleChange(param.name, e.target.value)}
                        >
                            {param.options.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            );
        } else if (param.type === 'number') {
            return (
                <Grid item xs={12} sm={6} key={param.name}>
                    <TextField
                        fullWidth
                        label={param.label}
                        type="number"
                        value={value}
                        onChange={(e) => handleChange(param.name, e.target.value)}
                        onWheel={handleNumberInputWheel}
                        inputProps={{
                            step: param.step || '0.01',
                            min: param.min || '0'
                        }}
                    />
                </Grid>
            );
        } else if (param.type === 'text') {
            return (
                <Grid item xs={12} sm={6} key={param.name}>
                    <TextField
                        fullWidth
                        label={param.label}
                        type="text"
                        value={value}
                        onChange={(e) => handleChange(param.name, e.target.value)}
                    />
                </Grid>
            );
        } else if (param.type === 'checkbox') {
            return (
                <Grid item xs={12} key={param.name}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={Boolean(value)}
                                onChange={(e) => handleChange(param.name, e.target.checked)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#2563eb',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#2563eb',
                                    },
                                }}
                            />
                        }
                        label={param.label}
                    />
                </Grid>
            );
        }

        return null;
    };

    // Helper function to group parameters by group name
    const renderTemplateParameters = (templateToRender) => {
        if (!templateToRender || !templateToRender.parameters) return null;

        // Filter out symbol parameters (they're shown in Step 2, not Step 3)
        const filteredParams = templateToRender.parameters.filter(param =>
            !(param.type === 'text' &&
                (param.name.toLowerCase().includes('symbol') || param.label.toLowerCase().includes('symbol')))
        );

        // Group parameters by their group field
        const groupedParams = filteredParams.reduce((acc, param) => {
            const group = param.group || 'General';
            if (!acc[group]) {
                acc[group] = [];
            }
            acc[group].push(param);
            return acc;
        }, {});

        return Object.entries(groupedParams).map(([groupName, params]) => (
            <Box key={groupName} sx={{ marginBottom: 3 }}>
                <Typography variant="subtitle1" sx={{ marginBottom: 2, fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>
                    {groupName}
                </Typography>
                <Grid container spacing={2}>
                    {params.map(param => renderDynamicField(param))}
                </Grid>
            </Box>
        ));
    };

    // Helper function to render template parameters in review (Step 4)
    const renderTemplateReview = (templateToReview) => {
        if (!templateToReview || !templateToReview.parameters) return null;

        // Filter out symbol parameters (already shown in Symbols section)
        const nonSymbolParams = templateToReview.parameters.filter(param =>
            !(param.type === 'text' &&
                (param.name.toLowerCase().includes('symbol') || param.label.toLowerCase().includes('symbol')))
        );

        // Group parameters by group
        const groupedParams = nonSymbolParams.reduce((acc, param) => {
            const group = param.group || 'General';
            if (!acc[group]) {
                acc[group] = [];
            }
            acc[group].push(param);
            return acc;
        }, {});

        return (
            <>
                {Object.entries(groupedParams).map(([groupName, params]) => (
                    <Box key={groupName} sx={{ marginBottom: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)', marginBottom: 2 }}>
                            {groupName}
                        </Typography>
                        <Grid container spacing={2}>
                            {params.map((param) => {
                                const value = formData[param.name];

                                // Show all parameters with their values (including defaults)
                                const displayValue = value !== undefined && value !== null && value !== ''
                                    ? value
                                    : (param.defaultValue !== undefined ? param.defaultValue : '');

                                // Skip if still no value
                                if (displayValue === '' || displayValue === null || displayValue === undefined) {
                                    return null;
                                }

                                // Format boolean values
                                let formattedValue = displayValue;
                                if (param.type === 'checkbox') {
                                    formattedValue = displayValue ? 'Yes' : 'No';
                                } else if (param.type === 'select') {
                                    // Find the label for select fields
                                    const option = param.options?.find(opt => opt.value === displayValue);
                                    formattedValue = option ? option.label : displayValue;
                                }

                                return (
                                    <Grid item xs={6} sm={4} key={param.name}>
                                        <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>
                                            {param.label}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>
                                            {formattedValue}
                                        </Typography>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                ))}
            </>
        );
    };

    const handleUserChange = (userId) => {
        const user = users.find(u => u.id === userId);
        handleChange('userId', userId);
        handleChange('userName', user ? `${user.firstName || user.name} ${user.lastName || ''}`.trim() : '');
        // Reset account when user changes
        handleChange('accountNumber', '');
    };

    const handleGenerateMagicId = () => {
        const randomId = Math.floor(100000 + Math.random() * 900000);
        handleChange('magicId', randomId.toString());
    };

    const handleSubmit = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error('Authentication token not found');
            return;
        }

        try {
            setSubmitting(true);

            // Get current user info from localStorage or context
            const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
            const currentUserName = userInfo.firstName ? `${userInfo.firstName} ${userInfo.lastName}` : 'Admin User';

            // Prepare strategy data according to backend API format (matching Settings strategy creation)
            const strategyData = {
                name: formData.strategyName,
                description: formData.description || '',
                accountNumber: formData.accountNumber,
                strategyType: 'goldarb', // Fixed strategy type
                isEnabled: true,
                isMultiSymbol: formData.isMultiSymbol,
                dashboardType: formData.dashboardType || 'Algo.01',
                createdBy: currentUserName,
                lastModifiedBy: currentUserName,
                symbols: [formData.spotSymbol, formData.futuresSymbol], // Array of symbols
                parameters: template ? (() => {
                    // Template Parameters - dynamically build from template definition
                    const templateParams = {
                        version: template.version,
                        Enabled: true,
                        Mode: 1,
                        FuturesSymbol: formData.futuresSymbol,
                        SpotSymbol: formData.spotSymbol
                    };

                    // Add each template parameter to the parameters object
                    template.parameters.forEach(param => {
                        const value = formData[param.name];

                        if (value !== undefined && value !== null && value !== '') {
                            // Map parameter name to backend format (e.g., baseLotSize -> GridManagement.BaseLotSize)
                            if (param.type === 'number') {
                                templateParams[`GridManagement.${param.name.charAt(0).toUpperCase() + param.name.slice(1)}`] = parseFloat(value) || 0;
                            } else if (param.type === 'checkbox') {
                                templateParams[`GridManagement.${param.name.charAt(0).toUpperCase() + param.name.slice(1)}`] = Boolean(value);
                            } else {
                                templateParams[`GridManagement.${param.name.charAt(0).toUpperCase() + param.name.slice(1)}`] = value;
                            }
                        }
                    });

                    // Add default risk management and spread validation if not in template
                    if (!templateParams['RiskManagement.Enabled']) {
                        templateParams['RiskManagement.Enabled'] = false;
                        templateParams['RiskManagement.OrderCooldownMs'] = 220;
                        templateParams['RiskManagement.OrderCooldownSeconds'] = 0;
                        templateParams['RiskManagement.RequireMinimumBars'] = false;
                        templateParams['RiskManagement.WarmupMinutes'] = 0;
                    }

                    if (!templateParams['SpreadValidation.Enabled']) {
                        templateParams['SpreadValidation.Enabled'] = false;
                        templateParams['SpreadValidation.MinAcceptableSpread'] = -1000;
                        templateParams['SpreadValidation.MaxAcceptableSpread'] = 1000;
                        templateParams['SpreadValidation.ExpectedAverageSpread'] = 0;
                    }

                    // Handle OpenNow parameter specially
                    const openNowValue = formData.openNow || 'none';
                    templateParams['OpenNow_BuySpread'] = openNowValue === 'buy' ? 'buy' : 'none';
                    templateParams['OpenNow_SellSpread'] = openNowValue === 'sell' ? 'sell' : 'none';

                    return templateParams;
                })() : formData.version === 'V2' ? {
                    // V2 Parameters
                    version: 'v2',
                    Enabled: true,
                    Mode: 1,
                    FuturesSymbol: formData.futuresSymbol,
                    SpotSymbol: formData.spotSymbol,

                    // Grid Management
                    'GridManagement.BidirectionalMode': Boolean(formData.bidirectionalMode),
                    'GridManagement.MaxTotalLotBothDirections': parseFloat(formData.maxTotalLotBothDirections) || 0.30,
                    'GridManagement.UseBenchmarkMode': Boolean(formData.useBenchmarkMode),
                    'GridManagement.BenchmarkThresholdBuy': parseFloat(formData.benchmarkThresholdBuy) || 4.0,
                    'GridManagement.BenchmarkThresholdSell': parseFloat(formData.benchmarkThresholdSell) || 4.0,
                    'GridManagement.GridThreshold': parseInt(formData.gridThreshold) || 2,
                    'GridManagement.MaxGridLevels': parseInt(formData.maxGridLevels) || 5,
                    'GridManagement.BuyBaseLotSize': parseFloat(formData.buyBaseLotSize) || 0.01,
                    'GridManagement.SellBaseLotSize': parseFloat(formData.sellBaseLotSize) || 0.01,
                    'GridManagement.BuyLotSizeMultiplier': parseFloat(formData.buyLotSizeMultiplier) || 1.2,
                    'GridManagement.SellLotSizeMultiplier': parseFloat(formData.sellLotSizeMultiplier) || 1.2,
                    'GridManagement.MaxLotOpenedAllowed': parseFloat(formData.v2MaxLotOpenedAllowed) || 0.15,
                    'GridManagement.BuyMaxLotOpenedAllowed': parseFloat(formData.v2MaxLotOpenedAllowed) || 0.15,
                    'GridManagement.SellMaxLotOpenedAllowed': parseFloat(formData.v2MaxLotOpenedAllowed) || 0.15,
                    'GridManagement.TPGridEnabled': Boolean(formData.v2TpGridEnabled),
                    'GridManagement.TPGridPips': parseInt(formData.tpGridPips) || 10,
                    'GridManagement.TPMode': formData.tpMode || 'AverageSpread',
                    'GridManagement.StopLossEnabled': Boolean(formData.v2StopLossEnabled),
                    'GridManagement.StopLossPips': parseInt(formData.stopLossPips) || 20,
                    'GridManagement.BuyGrid': parseFloat(formData.buyGrid) || 1.500,
                    'GridManagement.SellGrid': parseFloat(formData.sellGrid) || 1.500,
                    'GridManagement.BuyLegTP': parseFloat(formData.buyLegTP) || 0.300,
                    'GridManagement.SellLegTP': parseFloat(formData.sellLegTP) || 0.300,
                    'GridManagement.BuyLegSL': parseFloat(formData.buyLegSL) || 0.500,
                    'GridManagement.SellLegSL': parseFloat(formData.sellLegSL) || 0.500,
                    'GridManagement.AccumulativeTP': parseFloat(formData.accumulativeTP) || 2.5,
                    'GridManagement.AccumulativeSL': parseFloat(formData.accumulativeSL) || -10.0,
                    'GridManagement.BuyGridAllowed': true,
                    'GridManagement.SellGridAllowed': true,
                    'GridManagement.AllowGridReopening': false,
                    'GridManagement.ResetGridOnFullClose': false,

                    // Risk Management
                    'RiskManagement.Enabled': formData.riskManagementEnabled || false,
                    'RiskManagement.OrderCooldownMs': 220,
                    'RiskManagement.OrderCooldownSeconds': 0,
                    'RiskManagement.RequireMinimumBars': false,
                    'RiskManagement.WarmupMinutes': 0,

                    // Spread Validation
                    'SpreadValidation.Enabled': formData.spreadValidationEnabled || false,
                    'SpreadValidation.MinAcceptableSpread': parseFloat(formData.minAcceptableSpread) || -1000,
                    'SpreadValidation.MaxAcceptableSpread': parseFloat(formData.maxAcceptableSpread) || 1000,
                    'SpreadValidation.ExpectedAverageSpread': 0,

                    // Beta Spread defaults
                    'BetaSpread.RollingWindowSize': 100,
                    'BetaSpread.MedianFilterSize': 5,
                    'BetaSpread.LowerPercentile': 25,
                    'BetaSpread.UpperPercentile': 75,

                    // Open Now
                    'OpenNow_BuySpread': 'none',
                    'OpenNow_SellSpread': 'none'
                } : {
                    // V1 Parameters
                    version: 'v1',
                    Enabled: true,
                    Mode: formData.spreadDirection === 'Spot-Futures' ? 1 : 2,
                    FuturesSymbol: formData.futuresSymbol,
                    SpotSymbol: formData.spotSymbol,

                    'GridManagement.BaseLotSize': parseFloat(formData.baseLotSize) || 0.01,
                    'GridManagement.MinLotSize': parseFloat(formData.minLotSize) || 0.01,
                    'GridManagement.MaxLotOpenedAllowed': parseFloat(formData.maxLotOpenedAllowed) || 0,
                    'GridManagement.GridThreshold': parseFloat(formData.gridThreshold) || 0,
                    'GridManagement.MaxGridLevels': parseInt(formData.maxGridLevels) || 5,
                    'GridManagement.TPGridPips': parseFloat(formData.tpGridPips) || 0,
                    'GridManagement.StopLossPips': parseFloat(formData.stopLossPips) || 0,
                    'GridManagement.TPGridEnabled': formData.tpGridEnabled || false,
                    'GridManagement.StopLossEnabled': formData.stopLossEnabled || false,
                    'GridManagement.AllowGridReopening': formData.allowGridReopening || false,
                    'GridManagement.ResetGridOnFullClose': formData.resetGridOnFullClose || false,

                    'OrderTimeInForce': formData.orderTimeInForce || 'FOK',
                    'OpenNow_BuySpread': formData.openNow || 'none',
                    'OpenNow_SellSpread': formData.openNow || 'none',

                    // Risk Management defaults
                    'RiskManagement.Enabled': false,
                    'RiskManagement.OrderCooldownMs': 220,
                    'RiskManagement.OrderCooldownSeconds': 0,
                    'RiskManagement.RequireMinimumBars': false,
                    'RiskManagement.WarmupMinutes': 0,

                    // Spread Validation defaults
                    'SpreadValidation.Enabled': false,
                    'SpreadValidation.MinAcceptableSpread': -1000,
                    'SpreadValidation.MaxAcceptableSpread': 1000,
                    'SpreadValidation.ExpectedAverageSpread': 0
                }
            };

            console.log('Creating strategy with data:', strategyData);

            const response = await addStrategy(token, strategyData);

            if (response.success || response.data) {
                toast.success(`Strategy "${formData.strategyName}" created successfully!`);

                // Call onSave callback if provided
                if (onSave) {
                    onSave(response.data || response);
                }

                handleClose();
            } else {
                throw new Error(response.error || 'Failed to create strategy');
            }
        } catch (error) {
            console.error('Error creating strategy:', error);
            const errorMessage = error.response?.data?.title || error.message || 'Failed to create strategy. Please try again.';
            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setActiveStep(0);
        setFormData({
            userId: '',
            userName: '',
            version: 'V2',
            strategyName: '',
            description: '',
            accountNumber: '',
            magicId: '',
            dashboardType: 'Algo.01',
            isMultiSymbol: true,
            futuresSymbol: '',
            spotSymbol: '',
            // V2 Parameters
            buyGrid: '1.500',
            sellGrid: '1.500',
            buyLegTP: '0.300',
            sellLegTP: '0.300',
            buyLegSL: '0.500',
            sellLegSL: '0.500',
            accumulativeTP: '2.5',
            accumulativeSL: '-10.0',
            benchmarkThresholdBuy: '4.0',
            benchmarkThresholdSell: '4.0',
            buyBaseLotSize: '0.01',
            sellBaseLotSize: '0.01',
            buyLotSizeMultiplier: '1.2',
            sellLotSizeMultiplier: '1.2',
            v2MaxLotOpenedAllowed: '0.15',
            tpMode: 'AverageSpread',
            v2TpGridEnabled: true,
            v2StopLossEnabled: true,
            riskManagementEnabled: false,
            spreadValidationEnabled: true,
            minAcceptableSpread: '-1000',
            maxAcceptableSpread: '1000',
            // V1 Parameters
            spreadDirection: 'Buy Spread',
            baseLotSize: '0.01',
            minLotSize: '0.01',
            maxLotOpenedAllowed: '0.05',
            gridThreshold: '2',
            maxGridLevels: '3',
            tpGridPips: '10',
            stopLossPips: '20',
            tpGridEnabled: false,
            stopLossEnabled: false,
            allowGridReopening: false,
            resetGridOnFullClose: false,
            orderTimeInForce: 'FOK',
            openNow: 'none',
            reviewed: false
        });
        onClose();
    };

    const isStepValid = () => {
        switch (activeStep) {
            case 0:
                return formData.userId && formData.version && formData.selectedTemplateId && formData.strategyName && formData.accountNumber;
            case 1:
                // Require at least one symbol to be selected
                return formData.selectedSymbols && formData.selectedSymbols.length > 0;
            case 2:
                // Step 3: Trading Parameters are optional - can be empty or filled
                return true;
            case 3:
                return true;
            default:
                return false;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
                    background: 'rgba(41, 43, 47, 1)',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                }
            }}
        >
            <DialogTitle sx={{
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                padding: 3,
                background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#ffffff' }}>
                            {template ? `Assign Template: ${template.name}` : 'Create New Strategy'}
                        </Typography>
                        {template && (
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', marginTop: 0.5 }}>
                                {template.icon} {template.description}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={handleClose} sx={{ color: '#ffffff' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <Box sx={{ padding: 3, background: 'rgba(51, 53, 57, 1)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Stepper
                    activeStep={activeStep}
                    sx={{
                        '& .MuiStepLabel-label': {
                            color: 'rgba(171, 171, 171, 1)'
                        },
                        '& .MuiStepLabel-label.Mui-completed': {
                            color: 'rgba(255, 255, 255, 1)'
                        },
                        '& .MuiStepLabel-label.Mui-active': {
                            color: 'rgba(255, 255, 255, 1)'
                        }
                    }}
                >
                    {steps.map((label, index) => (
                        <Step key={label}>
                            <StepLabel
                                StepIconProps={{
                                    sx: {
                                        '&.Mui-completed': {
                                            color: '#7c3aed'
                                        },
                                        '&.Mui-active': {
                                            color: '#7c3aed'
                                        }
                                    }
                                }}
                            >
                                {label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <Box sx={{ marginTop: 2, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'rgba(171, 171, 171, 1)' }}>
                        Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
                    </Typography>
                </Box>
            </Box>

            <DialogContent sx={{
                padding: 4,
                background: 'rgba(41, 43, 47, 1)',
                '& .MuiTypography-root': {
                    color: 'rgba(255, 255, 255, 1) !important'
                },
                '& .MuiTypography-caption': {
                    color: 'rgba(171, 171, 171, 1)'
                },
                '& .MuiInputLabel-root': {
                    color: 'rgba(171, 171, 171, 1)'
                },
                '& .MuiInputLabel-root.Mui-focused': {
                    color: '#7c3aed'
                },
                '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 1)',
                    '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)'
                    },
                    '&:hover fieldset': {
                        borderColor: 'rgba(124, 58, 237, 0.5)'
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: '#7c3aed'
                    }
                },
                '& .MuiCard-root': {
                    background: 'rgba(51, 53, 57, 1)',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                },
                '& .MuiAlert-root': {
                    background: 'rgba(0, 156, 214, 0.15)',
                    color: 'rgba(255, 255, 255, 1)',
                    border: '1px solid rgba(0, 156, 214, 0.3)'
                },
                '& .MuiChip-root': {
                    background: 'rgba(124, 58, 237, 0.15)',
                    color: '#7c3aed',
                    borderColor: 'rgba(124, 58, 237, 0.3)'
                },
                '& .MuiMenuItem-root': {
                    color: 'rgba(255, 255, 255, 1)',
                    '&:hover': {
                        background: 'rgba(124, 58, 237, 0.1)'
                    }
                },
                '& .MuiMenu-paper': {
                    background: 'rgba(51, 53, 57, 1)',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                }
            }}>
                {/* Step 1: Basic Information */}
                {activeStep === 0 && (
                    <Box>
                        <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 600, color: '#1e293b' }}>
                            Basic Information
                        </Typography>

                        {/* D3: Required fields indicator */}
                        <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', marginBottom: 3 }}>
                            * Required fields
                        </Typography>

                        <FormControl fullWidth sx={{ marginBottom: 3 }} error={!!validationErrors.userId}>
                            <InputLabel error={!!validationErrors.userId}>Select User *</InputLabel>
                            <Select
                                value={formData.userId}
                                label="Select User *"
                                onChange={(e) => handleUserChange(e.target.value)}
                                disabled={loadingUsers || !!preSelectedUser}
                                error={!!validationErrors.userId}
                            >
                                {loadingUsers ? (
                                    <MenuItem disabled>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CircularProgress size={16} />
                                            <Typography>Loading users...</Typography>
                                        </Box>
                                    </MenuItem>
                                ) : users.length === 0 ? (
                                    <MenuItem disabled>No users available</MenuItem>
                                ) : (
                                    users.map((user) => (
                                        <MenuItem key={user.id} value={user.id}>
                                            {user.firstName || user.name} {user.lastName || ''} ({user.email})
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                            {validationErrors.userId ? (
                                <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5 }}>
                                    {validationErrors.userId}
                                </Typography>
                            ) : preSelectedUser && (
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }}>
                                    User is pre-selected from assignment
                                </Typography>
                            )}
                        </FormControl>

                        <FormControl fullWidth sx={{ marginBottom: 3 }} error={!!validationErrors.selectedTemplateId}>
                            <InputLabel sx={{ color: validationErrors.selectedTemplateId ? '#ef4444' : 'rgba(255, 255, 255, 0.7)' }}>Strategy Template *</InputLabel>
                            <Select
                                value={formData.selectedTemplateId || ''}
                                label="Strategy Template *"
                                error={!!validationErrors.selectedTemplateId}
                                onChange={(e) => {
                                    const selectedTemplateId = e.target.value;

                                    // Auto-fill form based on selected template
                                    const selectedTemplate = getTemplateById(selectedTemplateId);
                                    console.log('✅ Selected Template:', selectedTemplate);
                                    if (selectedTemplate) {
                                        // Extract symbol parameters from template
                                        const symbolParams = selectedTemplate.parameters?.filter(param =>
                                            param.type === 'text' &&
                                            (param.name.toLowerCase().includes('symbol') || param.label.toLowerCase().includes('symbol'))
                                        ) || [];

                                        // Get default values for symbols
                                        const extractedSymbols = symbolParams
                                            .map(param => param.defaultValue)
                                            .filter(val => val && val.trim() !== '');

                                        console.log('📊 Extracted Symbols:', extractedSymbols);

                                        // Build default values object from all template parameters
                                        const defaultValues = {};
                                        selectedTemplate.parameters?.forEach(param => {
                                            if (param.defaultValue !== undefined && param.defaultValue !== null) {
                                                defaultValues[param.name] = param.defaultValue;
                                            }
                                        });

                                        console.log('✨ Default Values:', defaultValues);

                                        setFormData(prev => ({
                                            ...prev,
                                            selectedTemplateId: selectedTemplateId,
                                            strategyType: selectedTemplate.strategyType || selectedTemplateId,
                                            strategyName: selectedTemplate.name,
                                            description: selectedTemplate.description,
                                            version: selectedTemplate.version === 'v1' ? 'V1' : 'V2',
                                            isMultiSymbol: selectedTemplate.isMultiSymbol !== undefined ? selectedTemplate.isMultiSymbol : true,
                                            selectedSymbols: extractedSymbols.length > 0 ? extractedSymbols : prev.selectedSymbols,
                                            ...defaultValues // Spread all default values into formData
                                        }));
                                    }
                                }}
                                disabled={!!template}
                                sx={{
                                    color: '#ffffff',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255, 255, 255, 0.23)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255, 255, 255, 0.4)',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#667eea',
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: 'rgba(255, 255, 255, 0.7)',
                                    }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            bgcolor: '#1e293b',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            maxHeight: 400
                                        }
                                    }
                                }}
                            >
                                {(() => {
                                    const templates = getAllTemplates();
                                    console.log('📋 Available Templates:', templates);
                                    if (templates.length === 0) {
                                        return <MenuItem disabled>No templates available</MenuItem>;
                                    }
                                    return templates.map((tmpl) => (
                                        <MenuItem
                                            key={tmpl.templateId}
                                            value={tmpl.templateId}
                                            sx={{
                                                display: 'block',
                                                py: 1.5,
                                                px: 2,
                                                bgcolor: '#1e293b',
                                                color: '#ffffff',
                                                '&:hover': {
                                                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                                                },
                                                '&.Mui-selected': {
                                                    bgcolor: 'rgba(102, 126, 234, 0.2)',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(102, 126, 234, 0.3)',
                                                    }
                                                }
                                            }}
                                        >
                                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#ffffff', mb: 0.5 }}>
                                                {tmpl.icon || '📊'} {tmpl.name}
                                            </Typography>
                                            <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)' }}>
                                                {tmpl.description} • {tmpl.version}
                                            </Typography>
                                        </MenuItem>
                                    ));
                                })()}
                            </Select>
                            {validationErrors.selectedTemplateId ? (
                                <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5 }}>
                                    {validationErrors.selectedTemplateId}
                                </Typography>
                            ) : template && (
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }}>
                                    Strategy template is pre-selected
                                </Typography>
                            )}
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Strategy Name *"
                            value={formData.strategyName}
                            onChange={(e) => handleChange('strategyName', e.target.value)}
                            error={!!validationErrors.strategyName}
                            InputProps={{
                                readOnly: true,
                            }}
                            sx={{
                                marginBottom: 3,
                                '& .MuiInputBase-input': {
                                    cursor: 'not-allowed',
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                }
                            }}
                            placeholder="Select a template to auto-fill"
                            helperText={validationErrors.strategyName || "Strategy name is automatically set from the selected template"}
                        />

                        <TextField
                            fullWidth
                            label="Description (Optional)"
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            multiline
                            rows={3}
                            sx={{ marginBottom: 3 }}
                            placeholder="Describe your strategy..."
                        />

                        <FormControl fullWidth sx={{ marginBottom: 3 }} error={!!validationErrors.accountNumber}>
                            <InputLabel error={!!validationErrors.accountNumber}>Select Account *</InputLabel>
                            <Select
                                value={formData.accountNumber}
                                label="Select Account *"
                                onChange={(e) => handleChange('accountNumber', e.target.value)}
                                disabled={!formData.userId || loadingAccounts || !!preSelectedAccount}
                                error={!!validationErrors.accountNumber}
                            >
                                {loadingAccounts ? (
                                    <MenuItem disabled>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CircularProgress size={16} />
                                            <Typography>Loading accounts...</Typography>
                                        </Box>
                                    </MenuItem>
                                ) : !formData.userId ? (
                                    <MenuItem disabled>Please select a user first</MenuItem>
                                ) : accounts.length === 0 ? (
                                    <MenuItem disabled>No accounts available for this user</MenuItem>
                                ) : (
                                    accounts.map((account) => (
                                        <MenuItem key={account.id || account.accountNumber} value={account.accountNumber}>
                                            {account.accountNumber} - {account.lpName || account.name || 'Account'} ({account.accountType || account.type || 'N/A'})
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                            {validationErrors.accountNumber ? (
                                <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5 }}>
                                    {validationErrors.accountNumber}
                                </Typography>
                            ) : preSelectedAccount ? (
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }}>
                                    Account is pre-selected from assignment
                                </Typography>
                            ) : formData.userId && accounts.length > 0 && (
                                <Typography variant="caption" sx={{ color: '#64748b', marginTop: 0.5 }}>
                                    ℹ️ Showing {accounts.length} account(s) for selected user
                                </Typography>
                            )}
                        </FormControl>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', marginBottom: 3 }}>
                            <TextField
                                fullWidth
                                label="Magic ID"
                                value={formData.magicId}
                                onChange={(e) => handleChange('magicId', e.target.value)}
                                placeholder="Leave empty to auto-generate"
                                helperText="Unique identifier for this strategy (auto-generated if left empty)"
                                InputLabelProps={{
                                    sx: { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': {
                                            borderColor: 'rgba(255, 255, 255, 0.23)'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(255, 255, 255, 0.4)'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#667eea'
                                        }
                                    },
                                    '& .MuiFormHelperText-root': {
                                        color: 'rgba(255, 255, 255, 0.5)'
                                    }
                                }}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<CasinoIcon />}
                                onClick={handleGenerateMagicId}
                                sx={{
                                    textTransform: 'none',
                                    minWidth: 160,
                                    height: 56,
                                    borderColor: '#667eea',
                                    color: '#667eea',
                                    '&:hover': {
                                        borderColor: '#5568d3',
                                        background: 'rgba(102, 126, 234, 0.1)'
                                    }
                                }}
                            >
                                Auto-Generate
                            </Button>
                        </Box>

                        <FormControl fullWidth sx={{ marginBottom: 3 }} error={!!validationErrors.dashboardType}>
                            <InputLabel sx={{ color: validationErrors.dashboardType ? '#ef4444' : 'rgba(255, 255, 255, 0.7)' }}>Dashboard Type *</InputLabel>
                            <Select
                                value={formData.dashboardType}
                                label="Dashboard Type *"
                                onChange={(e) => handleChange('dashboardType', e.target.value)}
                                error={!!validationErrors.dashboardType}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            background: 'rgba(41, 43, 47, 1)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            '& .MuiMenuItem-root': {
                                                color: '#ffffff',
                                                '&:hover': {
                                                    background: 'rgba(124, 58, 237, 0.2)'
                                                },
                                                '&.Mui-selected': {
                                                    background: 'rgba(124, 58, 237, 0.3)',
                                                    '&:hover': {
                                                        background: 'rgba(124, 58, 237, 0.4)'
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }}
                            >
                                <MenuItem value="Algo.01">Algo.01 - Legacy</MenuItem>
                                <MenuItem value="Algo.02">Algo.02 - Analytics</MenuItem>
                                <MenuItem value="Algo.03">Algo.03 - Reserved</MenuItem>
                                <MenuItem value="Algo.04">Algo.04 - Reserved</MenuItem>
                            </Select>
                            {validationErrors.dashboardType && (
                                <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5 }}>
                                    {validationErrors.dashboardType}
                                </Typography>
                            )}
                        </FormControl>

                        <Box sx={{
                            padding: 2,
                            background: 'rgba(124, 58, 237, 0.05)',
                            borderRadius: '8px',
                            border: '1px solid rgba(124, 58, 237, 0.2)'
                        }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isMultiSymbol}
                                        onChange={(e) => handleChange('isMultiSymbol', e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#2563eb',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#2563eb',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                            Multi-Symbol Strategy
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                                            {formData.isMultiSymbol
                                                ? 'Requires 2 symbols (Futures & Spot) for arbitrage trading'
                                                : 'Requires only 1 symbol for single-market trading'}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    </Box>
                )}

                {/* Step 2: Select Symbols */}
                {activeStep === 1 && (
                    <Box>
                        <Typography variant="h6" sx={{ marginBottom: 3, fontWeight: 600, color: '#1e293b' }}>
                            Select Trading Symbols *
                        </Typography>

                        <Alert severity="info" sx={{ marginBottom: 3 }}>
                            Select at least 2 symbols (Futures and Spot) from the dropdown. They will appear below.
                        </Alert>

                        {/* D2: Validation error for symbols */}
                        {validationErrors.selectedSymbols && (
                            <Alert severity="error" sx={{ marginBottom: 3 }}>
                                {validationErrors.selectedSymbols}
                            </Alert>
                        )}

                        <Autocomplete
                            fullWidth
                            options={[...(symbols.futures || []), ...(symbols.spot || [])].filter((symbol, index, self) =>
                                self.indexOf(symbol) === index // Remove duplicates
                            )}
                            getOptionLabel={(option) => option}
                            getOptionDisabled={(option) => (formData.selectedSymbols || []).includes(option)}
                            value={null}
                            onChange={(event, newValue) => {
                                if (newValue && !(formData.selectedSymbols || []).includes(newValue)) {
                                    setFormData(prev => ({
                                        ...prev,
                                        selectedSymbols: [...(prev.selectedSymbols || []), newValue]
                                    }));
                                    // Clear the input after selection
                                    event.target.blur();
                                    setTimeout(() => {
                                        const autocompleteInput = document.querySelector('[aria-label="Select Symbol"]');
                                        if (autocompleteInput) {
                                            autocompleteInput.value = '';
                                        }
                                    }, 0);
                                }
                            }}
                            disabled={loadingSymbols}
                            loading={loadingSymbols}
                            inputValue=""
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Symbol"
                                    placeholder="Click to select a symbol"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingSymbols ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            sx={{ marginBottom: 3 }}
                        />

                        {/* Display selected symbols as chips */}
                        {(formData.selectedSymbols || []).length > 0 && (
                            <Box sx={{
                                padding: 2,
                                background: 'rgba(31, 33, 37, 1)',
                                borderRadius: '8px',
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ffffff', marginBottom: 2 }}>
                                    Selected Symbols ({(formData.selectedSymbols || []).length}):
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {(formData.selectedSymbols || []).map((symbol, index) => (
                                        <Chip
                                            key={symbol}
                                            label={symbol}
                                            onDelete={() => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    selectedSymbols: (prev.selectedSymbols || []).filter(s => s !== symbol)
                                                }));
                                            }}
                                            sx={{
                                                background: index % 2 === 0 ? '#7c3aed' : '#06b6d4',
                                                color: '#ffffff',
                                                fontWeight: 600,
                                                '& .MuiChip-deleteIcon': {
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                    '&:hover': {
                                                        color: '#ffffff'
                                                    }
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Step 3: Trading Parameters */}
                {activeStep === 2 && (
                    <Box>
                        <Typography variant="h6" sx={{ marginBottom: 3, fontWeight: 600, color: '#1e293b' }}>
                            {template ? `${template.name} - Parameters` : `Trading Parameters (${formData.version})`}
                        </Typography>

                        {/* Symbol Selection Section */}
                        <Box sx={{ marginBottom: 4 }}>
                            <Typography variant="subtitle1" sx={{ marginBottom: 2, fontWeight: 600, color: '#1e293b' }}>
                                Symbol Configuration
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Symbol 1 (Futures) *</InputLabel>
                                        <Select
                                            value={formData.futuresSymbol || ''}
                                            label="Symbol 1 (Futures) *"
                                            onChange={(e) => handleChange('futuresSymbol', e.target.value)}
                                        >
                                            {(formData.selectedSymbols || []).map((symbol) => (
                                                <MenuItem key={symbol} value={symbol}>
                                                    {symbol}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {formData.isMultiSymbol && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Symbol 2 (Spot) *</InputLabel>
                                            <Select
                                                value={formData.spotSymbol || ''}
                                                label="Symbol 2 (Spot) *"
                                                onChange={(e) => handleChange('spotSymbol', e.target.value)}
                                            >
                                                {(formData.selectedSymbols || []).map((symbol) => (
                                                    <MenuItem key={symbol} value={symbol}>
                                                        {symbol}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>

                        {(() => {
                            // Get the current template (either passed as prop or selected from dropdown)
                            const currentTemplate = template || (formData.selectedTemplateId ? getTemplateById(formData.selectedTemplateId) : null);

                            if (currentTemplate) {
                                // Dynamic Template Parameters
                                return <>{renderTemplateParameters(currentTemplate)}</>;
                            } else if (formData.version === 'V2') {
                                // Fallback to V2 hardcoded parameters (legacy)
                                return (
                                    <>
                                        <Typography variant="subtitle1" sx={{ marginBottom: 2, fontWeight: 600, color: '#1e293b' }}>
                                            Grid Settings
                                        </Typography>
                                <Grid container spacing={2} sx={{ marginBottom: 3 }}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={Boolean(formData.bidirectionalMode)}
                                                    onChange={(e) => handleChange('bidirectionalMode', e.target.checked)}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: '#2563eb',
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: '#2563eb',
                                                        },
                                                    }}
                                                />
                                            }
                                            label="Bidirectional Mode"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Max Total Lot Both Directions"
                                            type="number"
                                            value={formData.maxTotalLotBothDirections}
                                            onChange={(e) => handleChange('maxTotalLotBothDirections', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.01', min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={Boolean(formData.useBenchmarkMode)}
                                                    onChange={(e) => handleChange('useBenchmarkMode', e.target.checked)}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: '#2563eb',
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: '#2563eb',
                                                        },
                                                    }}
                                                />
                                            }
                                            label="Use Benchmark Mode"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Benchmark Threshold Buy"
                                            type="number"
                                            value={formData.benchmarkThresholdBuy}
                                            onChange={(e) => handleChange('benchmarkThresholdBuy', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.1', min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Benchmark Threshold Sell"
                                            type="number"
                                            value={formData.benchmarkThresholdSell}
                                            onChange={(e) => handleChange('benchmarkThresholdSell', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.1', min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Grid Threshold"
                                            type="number"
                                            value={formData.gridThreshold}
                                            onChange={(e) => handleChange('gridThreshold', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '1', min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Max Grid Levels"
                                            type="number"
                                            value={formData.maxGridLevels}
                                            onChange={(e) => handleChange('maxGridLevels', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '1', min: '1' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Buy Base Lot Size"
                                            type="number"
                                            value={formData.buyBaseLotSize}
                                            onChange={(e) => handleChange('buyBaseLotSize', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.01', min: '0.01' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Sell Base Lot Size"
                                            type="number"
                                            value={formData.sellBaseLotSize}
                                            onChange={(e) => handleChange('sellBaseLotSize', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.01', min: '0.01' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Buy Lot Size Multiplier"
                                            type="number"
                                            value={formData.buyLotSizeMultiplier}
                                            onChange={(e) => handleChange('buyLotSizeMultiplier', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.1', min: '0.1' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Sell Lot Size Multiplier"
                                            type="number"
                                            value={formData.sellLotSizeMultiplier}
                                            onChange={(e) => handleChange('sellLotSizeMultiplier', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.1', min: '0.1' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Max Lot Opened Allowed"
                                            type="number"
                                            value={formData.v2MaxLotOpenedAllowed}
                                            onChange={(e) => handleChange('v2MaxLotOpenedAllowed', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.01', min: '0.01' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={Boolean(formData.v2TpGridEnabled)}
                                                    onChange={(e) => handleChange('v2TpGridEnabled', e.target.checked)}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: '#2563eb',
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: '#2563eb',
                                                        },
                                                    }}
                                                />
                                            }
                                            label="TP Grid Enabled"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="TP Grid Pips"
                                            type="number"
                                            value={formData.tpGridPips}
                                            onChange={(e) => handleChange('tpGridPips', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '1', min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>TP Mode</InputLabel>
                                            <Select
                                                value={formData.tpMode}
                                                label="TP Mode"
                                                onChange={(e) => handleChange('tpMode', e.target.value)}
                                            >
                                                <MenuItem value="AverageSpread">Average Spread</MenuItem>
                                                <MenuItem value="FixedPips">Fixed Pips</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={Boolean(formData.v2StopLossEnabled)}
                                                    onChange={(e) => handleChange('v2StopLossEnabled', e.target.checked)}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: '#2563eb',
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: '#2563eb',
                                                        },
                                                    }}
                                                />
                                            }
                                            label="Stop Loss Enabled"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Stop Loss Pips"
                                            type="number"
                                            value={formData.stopLossPips}
                                            onChange={(e) => handleChange('stopLossPips', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '1', min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Buy Grid"
                                            type="number"
                                            value={formData.buyGrid}
                                            onChange={(e) => handleChange('buyGrid', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.001', min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Sell Grid"
                                            type="number"
                                            value={formData.sellGrid}
                                            onChange={(e) => handleChange('sellGrid', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.001', min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Buy Leg TP"
                                            type="number"
                                            value={formData.buyLegTP}
                                            onChange={(e) => handleChange('buyLegTP', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.001', min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Sell Leg TP"
                                            type="number"
                                            value={formData.sellLegTP}
                                            onChange={(e) => handleChange('sellLegTP', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.001', min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Buy Leg SL"
                                            type="number"
                                            value={formData.buyLegSL}
                                            onChange={(e) => handleChange('buyLegSL', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.001', min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Sell Leg SL"
                                            type="number"
                                            value={formData.sellLegSL}
                                            onChange={(e) => handleChange('sellLegSL', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.001', min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Accumulative TP"
                                            type="number"
                                            value={formData.accumulativeTP}
                                            onChange={(e) => handleChange('accumulativeTP', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.1', min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Accumulative SL"
                                            type="number"
                                            value={formData.accumulativeSL}
                                            onChange={(e) => handleChange('accumulativeSL', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.1' }}
                                        />
                                    </Grid>
                                </Grid>

                                <Typography variant="subtitle1" sx={{ marginBottom: 2, fontWeight: 600, color: '#1e293b' }}>
                                    Risk Management
                                </Typography>
                                <Grid container spacing={2} sx={{ marginBottom: 3 }}>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.riskManagementEnabled}
                                                    onChange={(e) => handleChange('riskManagementEnabled', e.target.checked)}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: '#2563eb',
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: '#2563eb',
                                                        },
                                                    }}
                                                />
                                            }
                                            label="Enable Risk Management"
                                        />
                                    </Grid>
                                </Grid>

                                <Typography variant="subtitle1" sx={{ marginBottom: 2, fontWeight: 600, color: '#1e293b' }}>
                                    Spread Validation
                                </Typography>
                                <Grid container spacing={2} sx={{ marginBottom: 3 }}>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.spreadValidationEnabled}
                                                    onChange={(e) => handleChange('spreadValidationEnabled', e.target.checked)}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: '#2563eb',
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: '#2563eb',
                                                        },
                                                    }}
                                                />
                                            }
                                            label="Enable Spread Validation"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Min Acceptable Spread"
                                            type="number"
                                            value={formData.minAcceptableSpread}
                                            onChange={(e) => handleChange('minAcceptableSpread', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            disabled={!formData.spreadValidationEnabled}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Max Acceptable Spread"
                                            type="number"
                                            value={formData.maxAcceptableSpread}
                                            onChange={(e) => handleChange('maxAcceptableSpread', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            disabled={!formData.spreadValidationEnabled}
                                        />
                                    </Grid>
                                </Grid>
                            </>
                                );
                            } else {
                                // V1 Parameters (fallback - should not reach here if template is selected)
                                return (
                                    <>
                                        <Typography variant="subtitle1" sx={{ marginBottom: 2, fontWeight: 600, color: '#1e293b' }}>
                                            Grid Management
                                        </Typography>
                                <Grid container spacing={2} sx={{ marginBottom: 3 }}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Spread Direction</InputLabel>
                                            <Select
                                                value={formData.spreadDirection}
                                                label="Spread Direction"
                                                onChange={(e) => handleChange('spreadDirection', e.target.value)}
                                            >
                                                <MenuItem value="Buy Spread">Buy Spread</MenuItem>
                                                <MenuItem value="Sell Spread">Sell Spread</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Base Lot Size"
                                            type="number"
                                            value={formData.baseLotSize}
                                            onChange={(e) => handleChange('baseLotSize', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.01', min: '0.01' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Min Lot Size"
                                            type="number"
                                            value={formData.minLotSize}
                                            onChange={(e) => handleChange('minLotSize', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.01', min: '0.01' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Max Lot Opened Allowed"
                                            type="number"
                                            value={formData.maxLotOpenedAllowed}
                                            onChange={(e) => handleChange('maxLotOpenedAllowed', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ step: '0.01', min: '0.01' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Grid Threshold"
                                            type="number"
                                            value={formData.gridThreshold}
                                            onChange={(e) => handleChange('gridThreshold', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Max Grid Levels"
                                            type="number"
                                            value={formData.maxGridLevels}
                                            onChange={(e) => handleChange('maxGridLevels', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ min: '1' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="TP Grid Pips"
                                            type="number"
                                            value={formData.tpGridPips}
                                            onChange={(e) => handleChange('tpGridPips', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ min: '0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Stop Loss Pips"
                                            type="number"
                                            value={formData.stopLossPips}
                                            onChange={(e) => handleChange('stopLossPips', e.target.value)}
                                            onWheel={handleNumberInputWheel}
                                            inputProps={{ min: '0' }}
                                        />
                                    </Grid>
                                </Grid>

                                <Typography variant="subtitle1" sx={{ marginBottom: 2, fontWeight: 600, color: '#1e293b' }}>
                                    Core Settings
                                </Typography>
                                <Grid container spacing={2} sx={{ marginBottom: 3 }}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Order Time In Force</InputLabel>
                                            <Select
                                                value={formData.orderTimeInForce}
                                                label="Order Time In Force"
                                                onChange={(e) => handleChange('orderTimeInForce', e.target.value)}
                                            >
                                                <MenuItem value="GTC">GTC (Good Till Cancelled)</MenuItem>
                                                <MenuItem value="IOC">IOC (Immediate or Cancel)</MenuItem>
                                                <MenuItem value="FOK">FOK (Fill or Kill)</MenuItem>
                                                <MenuItem value="DAY">DAY (Day Order)</MenuItem>
                                                <MenuItem value="GTD">GTD (Good Till Date)</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Open Now</InputLabel>
                                            <Select
                                                value={formData.openNow}
                                                label="Open Now"
                                                onChange={(e) => handleChange('openNow', e.target.value)}
                                            >
                                                <MenuItem value="buy">Buy</MenuItem>
                                                <MenuItem value="sell">Sell</MenuItem>
                                                <MenuItem value="none">None</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                    </>
                                );
                            }
                        })()}
                    </Box>
                )}

                {/* Step 4: Review */}
                {activeStep === 3 && (
                    <Box>
                        <Typography variant="h6" sx={{ marginBottom: 3, fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>
                            Review Strategy Configuration
                        </Typography>

                        <Alert severity="success" sx={{ marginBottom: 3 }}>
                            Please review your strategy configuration before creating
                        </Alert>

                        <Box sx={{
                            background: 'rgba(41, 43, 47, 1)',
                            borderRadius: '12px',
                            padding: 3,
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)', marginBottom: 2 }}>
                                Basic Information
                            </Typography>
                            <Grid container spacing={2} sx={{ marginBottom: 3 }}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Strategy Name</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.strategyName}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Version</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.version}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>User</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.userName}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Account</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.accountNumber}</Typography>
                                </Grid>
                                {formData.magicId && (
                                    <Grid item xs={6}>
                                        <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Magic ID</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.magicId}</Typography>
                                    </Grid>
                                )}
                                {formData.description && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Description</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.description}</Typography>
                                    </Grid>
                                )}
                            </Grid>

                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)', marginBottom: 2 }}>
                                Symbols
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', marginBottom: 3 }}>
                                {formData.selectedSymbols && formData.selectedSymbols.length > 0 ? (
                                    formData.selectedSymbols.map((symbol, index) => (
                                        <Chip
                                            key={index}
                                            label={symbol}
                                            sx={{
                                                background: index % 2 === 0 ? '#7c3aed' : '#06b6d4',
                                                color: '#ffffff',
                                                fontWeight: 600
                                            }}
                                        />
                                    ))
                                ) : (
                                    <>
                                        {formData.futuresSymbol && <Chip label={`Futures: ${formData.futuresSymbol}`} sx={{ background: '#7c3aed', color: '#ffffff', fontWeight: 600 }} />}
                                        {formData.spotSymbol && <Chip label={`Spot: ${formData.spotSymbol}`} sx={{ background: '#06b6d4', color: '#ffffff', fontWeight: 600 }} />}
                                    </>
                                )}
                            </Box>

                            {(() => {
                                // Get the current template (either passed as prop or selected from dropdown)
                                const currentTemplate = template || (formData.selectedTemplateId ? getTemplateById(formData.selectedTemplateId) : null);

                                if (currentTemplate) {
                                    // Template Parameters Review
                                    return renderTemplateReview(currentTemplate);
                                } else if (formData.version === 'V2') {
                                    return (
                                <>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)', marginBottom: 2 }}>
                                        V2 Trading Parameters
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {/* Grid Settings */}
                                        {formData.buyGrid && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Buy Grid</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.buyGrid}</Typography>
                                            </Grid>
                                        )}
                                        {formData.sellGrid && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Sell Grid</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.sellGrid}</Typography>
                                            </Grid>
                                        )}
                                        {formData.buyLegTP && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Buy Leg TP</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.buyLegTP}</Typography>
                                            </Grid>
                                        )}
                                        {formData.sellLegTP && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Sell Leg TP</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.sellLegTP}</Typography>
                                            </Grid>
                                        )}
                                        {formData.buyLegSL && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Buy Leg SL</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.buyLegSL}</Typography>
                                            </Grid>
                                        )}
                                        {formData.sellLegSL && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Sell Leg SL</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.sellLegSL}</Typography>
                                            </Grid>
                                        )}
                                        {formData.accumulativeTP && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Accumulative TP</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.accumulativeTP}</Typography>
                                            </Grid>
                                        )}
                                        {formData.accumulativeSL && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Accumulative SL</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.accumulativeSL}</Typography>
                                            </Grid>
                                        )}

                                        {/* Lot Size Settings */}
                                        {formData.buyBaseLotSize && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Buy Base Lot Size</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.buyBaseLotSize}</Typography>
                                            </Grid>
                                        )}
                                        {formData.sellBaseLotSize && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Sell Base Lot Size</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.sellBaseLotSize}</Typography>
                                            </Grid>
                                        )}
                                        {formData.buyLotSizeMultiplier && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Buy Lot Size Multiplier</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.buyLotSizeMultiplier}</Typography>
                                            </Grid>
                                        )}
                                        {formData.sellLotSizeMultiplier && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Sell Lot Size Multiplier</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.sellLotSizeMultiplier}</Typography>
                                            </Grid>
                                        )}
                                        {formData.v2MaxLotOpenedAllowed && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Max Lot Opened Allowed</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.v2MaxLotOpenedAllowed}</Typography>
                                            </Grid>
                                        )}

                                        {/* Benchmark Settings */}
                                        {formData.benchmarkThresholdBuy && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Benchmark Threshold Buy</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.benchmarkThresholdBuy}</Typography>
                                            </Grid>
                                        )}
                                        {formData.benchmarkThresholdSell && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Benchmark Threshold Sell</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.benchmarkThresholdSell}</Typography>
                                            </Grid>
                                        )}

                                        {/* TP Mode */}
                                        {formData.tpMode && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>TP Mode</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.tpMode}</Typography>
                                            </Grid>
                                        )}

                                        {/* Grid Management */}
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>TP Grid Enabled</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.v2TpGridEnabled ? 'Yes' : 'No'}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Stop Loss Enabled</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.v2StopLossEnabled ? 'Yes' : 'No'}</Typography>
                                        </Grid>

                                        {/* Risk Management */}
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Risk Management</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.riskManagementEnabled ? 'Enabled' : 'Disabled'}</Typography>
                                        </Grid>

                                        {/* Spread Validation */}
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Spread Validation</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.spreadValidationEnabled ? 'Enabled' : 'Disabled'}</Typography>
                                        </Grid>
                                        {formData.spreadValidationEnabled && formData.minAcceptableSpread && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Min Acceptable Spread</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.minAcceptableSpread}</Typography>
                                            </Grid>
                                        )}
                                        {formData.spreadValidationEnabled && formData.maxAcceptableSpread && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Max Acceptable Spread</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.maxAcceptableSpread}</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </>
                                    );
                                } else {
                                    return (
                                        <>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)', marginBottom: 2 }}>
                                                V1 Trading Parameters
                                            </Typography>
                                    <Grid container spacing={2}>
                                        {formData.spreadDirection && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Spread Direction</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.spreadDirection}</Typography>
                                            </Grid>
                                        )}
                                        {formData.baseLotSize && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Base Lot Size</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.baseLotSize}</Typography>
                                            </Grid>
                                        )}
                                        {formData.minLotSize && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Min Lot Size</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.minLotSize}</Typography>
                                            </Grid>
                                        )}
                                        {formData.maxLotOpenedAllowed && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Max Lot Opened Allowed</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.maxLotOpenedAllowed}</Typography>
                                            </Grid>
                                        )}
                                        {formData.gridThreshold && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Grid Threshold</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.gridThreshold}</Typography>
                                            </Grid>
                                        )}
                                        {formData.maxGridLevels && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Max Grid Levels</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.maxGridLevels}</Typography>
                                            </Grid>
                                        )}
                                        {formData.tpGridPips && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>TP Grid Pips</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.tpGridPips}</Typography>
                                            </Grid>
                                        )}
                                        {formData.stopLossPips && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Stop Loss Pips</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.stopLossPips}</Typography>
                                            </Grid>
                                        )}

                                        {/* V1 Toggles */}
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>TP Grid Enabled</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.tpGridEnabled ? 'Yes' : 'No'}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Stop Loss Enabled</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.stopLossEnabled ? 'Yes' : 'No'}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Allow Grid Reopening</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.allowGridReopening ? 'Yes' : 'No'}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Reset Grid On Full Close</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.resetGridOnFullClose ? 'Yes' : 'No'}</Typography>
                                        </Grid>
                                        {formData.orderTimeInForce && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Order Time In Force</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.orderTimeInForce}</Typography>
                                            </Grid>
                                        )}
                                        {formData.openNow && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>Open Now</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>{formData.openNow}</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </>
                                    );
                                }
                            })()}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ padding: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)', background: 'rgba(51, 53, 57, 1)', justifyContent: 'space-between' }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        textTransform: 'none',
                        color: 'rgba(171, 171, 171, 1)',
                        '&:hover': {
                            background: 'rgba(255, 255, 255, 0.05)'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {activeStep > 0 && (
                        <Button
                            onClick={handleBack}
                            startIcon={<ArrowBackIcon />}
                            sx={{
                                textTransform: 'none',
                                color: 'rgba(255, 255, 255, 0.9)',
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                border: '1px solid',
                                '&:hover': {
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    borderColor: 'rgba(255, 255, 255, 0.5)'
                                }
                            }}
                        >
                            Back
                        </Button>
                    )}
                    {activeStep < steps.length - 1 ? (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            endIcon={<ArrowForwardIcon />}
                            disabled={!isStepValid()}
                            sx={{
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%)'
                                }
                            }}
                        >
                            Next: {steps[activeStep + 1]}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CheckIcon />}
                            disabled={submitting}
                            sx={{
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                                },
                                '&:disabled': {
                                    background: 'rgba(107, 114, 128, 0.5)',
                                    color: 'rgba(255, 255, 255, 0.5)'
                                }
                            }}
                        >
                            {submitting ? 'Creating Strategy...' : 'Create Strategy'}
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default CreateStrategyWizard;
